import {
  type RuleGraphConditionSubject,
  RuleGraphConditionSubjectSchema,
  type RuleGraphDto,
  type RuleGraphNode,
} from "@intelligems/ig-types";

/**
 * Branch `meta.condition` values that mean "this row always runs" — what a
 * model writes when it reaches for a word instead of the `null` the contract
 * asks for. Each one chips into "When cart contains Always", the same
 * unreadable sentence the "…anything" phrasing exists to prevent, so the
 * header reads them as NO chip and finishes its own sentence.
 *
 * A closed list of words that can ONLY mean always, compared normalized for
 * case and whitespace. Deliberately not a synonym matcher: a real targeting
 * value ("Any Weather Jacket") must keep its chip, so anything broader would
 * silently swallow the merchant's own words.
 */
const ALWAYS_MATCHING_CONDITIONS = new Set([
  "all",
  "all carts",
  "always",
  "any",
  "any cart",
  "anything",
  "every cart",
]);

/**
 * The condition text a node's header would chip, or undefined when the node
 * has nothing to chip: no condition, a blank one, or a word for "always".
 */
export const chipConditionOf = (
  node: RuleGraphNode | undefined
): string | undefined => {
  const value =
    typeof node?.meta?.condition === "string" ? node.meta.condition : undefined;
  if (value === undefined) return undefined;
  const normalized = value.trim().toLowerCase().replace(/\s+/g, " ");
  return normalized === "" || ALWAYS_MATCHING_CONDITIONS.has(normalized)
    ? undefined
    : value;
};

/**
 * Which request context a branch's condition is about. The contract leaves
 * `meta.conditionSubject` optional so stored graphs stay byte-identical, and
 * a value the renderer does not know (an older bundle reading a newer graph)
 * must not blank the header — both fall back to the cart, the subject every
 * graph spoke before subjects existed. Non-branch nodes have no condition
 * subject and read as cart too.
 */
export const conditionSubjectOf = (
  node: RuleGraphNode
): RuleGraphConditionSubject => {
  if (node.kind !== "branch") return "cart";
  const parsed = RuleGraphConditionSubjectSchema.safeParse(
    node.meta?.conditionSubject
  );
  return parsed.success ? parsed.data : "cart";
};

/**
 * The distinct subjects the graph's chipped branches condition on, in graph
 * order. Branches with nothing to chip contribute no subject: an
 * always-matching row is about no context at all.
 */
export const graphConditionSubjects = (
  graph: RuleGraphDto
): RuleGraphConditionSubject[] => {
  const subjects: RuleGraphConditionSubject[] = [];
  for (const node of graph.nodes) {
    if (node.kind !== "branch" || chipConditionOf(node) === undefined) continue;
    const subject = conditionSubjectOf(node);
    if (!subjects.includes(subject)) subjects.push(subject);
  }
  return subjects;
};

/**
 * The subject the input header speaks when it heads a row of its own. A rule
 * whose branches all condition on one context lets the input finish its
 * sentence in that context; anything else (no chipped branch, or a mix) reads
 * as the cart.
 */
export const inputHeaderSubject = (
  graph: RuleGraphDto
): RuleGraphConditionSubject => {
  const subjects = graphConditionSubjects(graph);
  return subjects.length === 1 ? subjects[0] : "cart";
};

/**
 * Whether the rule branches on the checkout delivery at all. A cart alone
 * cannot light a delivery-keyed branch, so this is what decides whether a
 * delivery modifier is offered alongside the test carts; a rule that never
 * reads `request.shipping.delivery` has nothing for one to change. No graph
 * yet means the answer is unknown, which reads as no.
 */
export const graphConditionsOnDelivery = (
  graph: RuleGraphDto | null
): boolean =>
  graph !== null && graphConditionSubjects(graph).includes("delivery");
